import { ConfigurationProvider } from "../lib/infrastructure/configuration/ConfigurationProvider";
import { HttpClient } from "../lib/infrastructure/http/HttpClient";
import { PageFileResolver } from "../lib/infrastructure/files/PageFileResolver";
import { isPageFileUrl } from "../lib/infrastructure/files/PageFileUrl";
import { UrlUtility } from "../lib/utils/UrlUtility";

/** Where the editor's uploads go: `POST api/Spaces/{spaceId}/Pages/{pageId}/PageFiles`. */
export interface PageFileTarget {
    spaceId: string | number;
    pageId: string | number;
}

export interface UploadedPageFile {
    url: string;
    name: string;
}

/**
 * An `onUploadFile` for the editor: the same multipart request as `PageFilesService.uploadFiles` (field `file`), one file at
 * a time, answering `{ url, name }` from the created `PageFileDTO`.
 */
export function pageFileUploader(target: PageFileTarget): (file: File) => Promise<UploadedPageFile> {
    return async (file) => {
        const body = new FormData();
        body.append("file", file, file.name);
        const url = UrlUtility.join(
            ConfigurationProvider.getConfiguration().App.BackendUrl,
            `/api/Spaces/${target.spaceId}/Pages/${target.pageId}/PageFiles`
        );
        const res = await HttpClient.sessionRequest<{ name?: string; url?: string }>({ method: "POST", url, data: body });
        if (!res.data?.url) throw new Error(`The upload of ${file.name} returned no file URL.`);
        return { url: res.data.url, name: res.data.name || file.name };
    };
}

/**
 * A `resolveImageUrl` for the editor: Ark attachments (which need the bearer, OrbitalPlan/18) become object URLs from the
 * app-wide attachment cache; any other URL is used as is. `release` hands every acquired URL back (call it on unmount).
 */
export function pageFileImageResolver(resolver: PageFileResolver = PageFileResolver.Global) {
    const acquired = new Set<string>();
    return {
        resolveImageUrl: (url: string): string | Promise<string> => {
            if (!isPageFileUrl(url)) return url;
            if (acquired.has(url)) resolver.cache.release(url);
            acquired.add(url);
            return resolver.cache.acquire(url);
        },
        release: () => {
            acquired.forEach((url) => resolver.cache.release(url));
            acquired.clear();
        },
    };
}

/** The first configured Atlas origin (`Atlas.Origins`), for the editor's `{origin}/embed/{guid}` diagrams. */
export function configuredAtlasOrigin(): string | undefined {
    try {
        return ConfigurationProvider.getConfiguration()?.Atlas?.Origins?.[0];
    } catch {
        return undefined;
    }
}
